import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { wholeDollars } from "./templateFormatters";

const countLabel = (item) => {
  if (item.skipped) return { label: "—", color: "var(--sl-text-quaternary)" };
  if (item.count > 0) return { label: `+${item.count}`, color: "var(--sl-text-brand)" };
  return { label: "Done", color: "var(--sl-text-success)" };
};

const STAFF_WEEK_DESCRIPTION =
  "Applies each room's event template to confirmed shows and creates draft open shifts for whatever's missing. Nobody is notified until you publish.";

const StaffWeekModal = ({
  preview,
  applyPath,
  venueOwnershipId,
  week,
  applyParams,
  title = "Staff this week",
  description = STAFF_WEEK_DESCRIPTION,
  children,
  onClose,
  onApplied,
}) => {
  const [saving, setSaving] = useState(false);
  const total = preview.total;

  const confirm = async () => {
    if (!total || saving) return;
    setSaving(true);
    let created;
    try {
      const { data } = await client.post(applyPath, applyParams || { venue_ownership_id: venueOwnershipId, week });
      created = data.created;
    } catch (error) {
      setSaving(false);
      toastError(error?.response?.data?.errors?.[0] || "Could not create the open shifts.");
      return;
    }
    toastSuccess(`Created ${created} open draft shifts. Fill them, then publish.`);
    onApplied();
  };

  return (
    <Modal
      open
      width={600}
      showClose={false}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <div className="d-flex align-items-center justify-content-between" style={{ gap: 12, width: "100%" }}>
          <span style={{ fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
            {preview.cost !== null && preview.cost !== undefined && total > 0 ? `Adds about ${wholeDollars(preview.cost)} scheduled labor` : ""}
          </span>
          <div className="d-flex" style={{ gap: 12 }}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirm} disabled={saving || !total}>
              {`Create ${total} open shifts`}
            </Button>
          </div>
        </div>
      }
    >
      {children}
      <div style={{ borderTop: "1px solid var(--sl-border-secondary)", margin: "0 -24px", maxHeight: "60vh", overflowY: "auto" }}>
        {preview.items.map((item) => {
          const count = countLabel(item);
          return (
            <div
              key={item.key ?? item.event_id}
              className="staff-week-item"
              style={{ display: "flex", gap: 16, justifyContent: "space-between", padding: "12px 24px", borderBottom: "1px solid var(--sl-border-secondary)", opacity: item.skipped ? 0.6 : 1 }}
            >
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{ fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary)" }}>{`${item.date_label} · ${item.room_label}`}</span>
                <span style={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, color: "var(--sl-text-primary)" }}>{item.title}</span>
                <span style={{ fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>{item.detail}</span>
              </div>
              <div style={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, color: count.color, whiteSpace: "nowrap" }}>{count.label}</div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

StaffWeekModal.propTypes = {
  preview: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        event_id: PropTypes.number,
        key: PropTypes.string,
        title: PropTypes.string.isRequired,
        date_label: PropTypes.string.isRequired,
        room_label: PropTypes.string.isRequired,
        detail: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        skipped: PropTypes.bool.isRequired,
      }),
    ).isRequired,
    total: PropTypes.number.isRequired,
    cost: PropTypes.string,
  }).isRequired,
  applyPath: PropTypes.string.isRequired,
  venueOwnershipId: PropTypes.number,
  week: PropTypes.string,
  applyParams: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  onApplied: PropTypes.func.isRequired,
};

export default StaffWeekModal;
